// ─────────────────────────────────────────────────────────────────────────────
// AI 트랙레코드 수학적 무결성 엔진 (Step 2 — Zero Math Contradiction)
// ─────────────────────────────────────────────────────────────────────────────
// 단순 수익률(Raw Return)은 항상 가격에서 직접 재계산한다:
//   Raw Return   = (P_realized − P_initial) / P_initial × 100
// 벤치마크 대비 알파(Alpha)는 명확히 분리 표기한다:
//   Alpha        = Raw Return − Benchmark Return
// 저장된 actual_return_pct는 신뢰하지 않는다 — 화면상 +18.4% 같은 오염값이
// 가격 경로($225.52 → $152.14, 실제 −32.5%)와 모순될 수 있기 때문이다.
// 모든 수치는 결정론적이며, 소수점 2자리로 반올림해 반올림 오차를 고정한다.
// ─────────────────────────────────────────────────────────────────────────────

export type TrackDirection = "BULLISH" | "BEARISH" | "NEUTRAL";

/** 검증 가능한 예측 레코드 — 가격과 벤치마크를 최소 입력으로 받는다. */
export interface VerifiedPredictionRecord {
  id: string;
  ticker: string;
  direction: TrackDirection;
  /** 예측 시점 가격 (P_initial) */
  initialPrice: number;
  /** 평가 시점 실현 가격 (P_realized). 미평가면 null. */
  realizedPrice: number | null;
  /** 벤치마크 수익률(%). 이미 % 단위로 정규화된 값 (예: 4.1 = +4.1%). */
  benchmarkReturnPct: number | null;
  horizonDays: number;
  /** 예측 동결 시각 (ISO 문자열) */
  frozenTimestamp: string;
  /** 평가 확정 시각 (ISO 문자열). 미평가면 null. */
  resolvedTimestamp: string | null;
}

/** 계산 결과 — Raw Return과 Alpha가 분리되어 수학적 모순이 없다. */
export interface ComputedOutcome {
  /** (P_realized − P_initial) / P_initial × 100 (%) */
  rawReturn: number | null;
  /** 벤치마크 수익률 (%) */
  benchmarkReturn: number | null;
  /** rawReturn − benchmarkReturn (%p) */
  alpha: number | null;
  /** Alpha 기준 적중 여부. 미평가·벤치마크 부재·중립이면 null. */
  isHit: boolean | null;
  /** 적중/실패 사유 문자열 */
  hitReason: string;
}

/** 반올림 고정: 소수점 2자리 (부동소수점 오차 제거) */
const round2 = (x: number): number => Math.round(x * 100) / 100;

/**
 * 방향성 문자열을 표준 3분류로 정규화한다.
 * STRONG/MODERATE_BULLISH → BULLISH, STRONG/MODERATE_BEARISH → BEARISH, 그 외 → NEUTRAL.
 */
export function toTrackDirection(raw: string | null | undefined): TrackDirection {
  const v = (raw ?? "").toUpperCase();
  if (v.includes("BULL")) return "BULLISH";
  if (v.includes("BEAR")) return "BEARISH";
  return "NEUTRAL";
}

/**
 * 단일 예측의 검증 결과를 계산한다.
 *
 * Hit 정의 (공식화):
 *  - BULLISH: Alpha > 0 (벤치마크 초과 성과)
 *  - BEARISH: Alpha < 0 (하락 헤지/회피 성과)
 *  - NEUTRAL: 방향성 콜이 아니므로 Hit/Miss 미판정(null)
 *
 * Raw Return은 가격에서 직접 재계산하여 저장된 actual_return_pct의 오염을
 * 원천 차단한다. 벤치마크가 없거나 미평가 상태면 alpha와 isHit는 null이다.
 */
export function computeVerifiedOutcome(record: VerifiedPredictionRecord): ComputedOutcome {
  // 미평가: 실현 가격이 없으면 수익률 자체가 존재하지 않는다.
  if (record.realizedPrice == null || record.initialPrice == null || record.initialPrice === 0) {
    return {
      rawReturn: null,
      benchmarkReturn: null,
      alpha: null,
      isHit: null,
      hitReason: "PENDING",
    };
  }

  const rawReturn = round2(((record.realizedPrice - record.initialPrice) / record.initialPrice) * 100);
  const benchmarkReturn = record.benchmarkReturnPct != null ? round2(record.benchmarkReturnPct) : null;

  // 벤치마크가 없으면 알파를 정의할 수 없다.
  if (benchmarkReturn == null) {
    return {
      rawReturn,
      benchmarkReturn,
      alpha: null,
      isHit: null,
      hitReason: "NO_BENCHMARK",
    };
  }

  const alpha = round2(rawReturn - benchmarkReturn);

  let isHit: boolean | null;
  if (record.direction === "BULLISH") {
    isHit = alpha > 0;
  } else if (record.direction === "BEARISH") {
    isHit = alpha < 0;
  } else {
    isHit = null; // 중립은 방향성 콜이 아님
  }

  const hitReason = isHit === null
    ? `Neutral stance (alpha ${alpha >= 0 ? "+" : ""}${alpha}%p)`
    : isHit
      ? `Alpha ${alpha >= 0 ? "+" : ""}${alpha}%p vs benchmark (${benchmarkReturn}%)`
      : `Failed to outperform benchmark (alpha ${alpha >= 0 ? "+" : ""}${alpha}%p)`;

  return { rawReturn, benchmarkReturn, alpha, isHit, hitReason };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHA-256 (동기식, 표준 FIPS 180-4) — 동결 원장 해시를 "진짜"로 검증한다.
// ─────────────────────────────────────────────────────────────────────────────

const SHA256_K: number[] = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/** 문자열의 SHA-256 다이제스트(hex, 소문자 64자리)를 반환한다. */
export function sha256Hex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  const bitLen = bytes.length * 8;
  const paddedLen = Math.ceil((bytes.length + 1 + 8) / 64) * 64;
  const padded = new Uint8Array(paddedLen);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const dv = new DataView(padded.buffer);
  dv.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000), false);
  dv.setUint32(paddedLen - 4, bitLen >>> 0, false);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  for (let i = 0; i < paddedLen; i += 64) {
    const w = new Array<number>(64);
    for (let j = 0; j < 16; j += 1) {
      w[j] = dv.getUint32(i + j * 4, false);
    }
    for (let j = 16; j < 64; j += 1) {
      const s0 = rotr(w[j - 15], 7) ^ rotr(w[j - 15], 18) ^ (w[j - 15] >>> 3);
      const s1 = rotr(w[j - 2], 17) ^ rotr(w[j - 2], 19) ^ (w[j - 2] >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let j = 0; j < 64; j += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + SHA256_K[j] + w[j]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((x) => x.toString(16).padStart(8, "0"))
    .join("");
}

/**
 * 동결 원장 해시 — 예측 시점에 고정되는(불변) 필드만을 정규화해 해싱한다.
 * 평가 결과(가격/수익률)는 해시에 포함하지 않아, 사후 조작이 불가능한 WORM
 * 성격을 유지한다.
 */
export function computeFrozenHash(record: VerifiedPredictionRecord): string {
  const canonical = [
    record.ticker.toUpperCase(),
    record.direction,
    record.initialPrice,
    record.frozenTimestamp,
  ].join("|");
  return sha256Hex(canonical);
}